import React from 'react';
import {
  type ViewStyle,
  AccessibilityInfo,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import Config from 'react-native-config';
import {defer} from 'lodash';

import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import {ProfileActiveAttribute} from '/component/base/ProfileActiveAttribute';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import ModalHeader from '/component/layout/ModalHeader';
import NavigationHeader from '/component/layout/NavigationHeader';
import {ProfilePage} from '/constant/TestIDs';
import {TESTING_OPTIONS} from '/enum/popover.enum';
import {EVENT_NAMES, SCREEN_NAMES, USER_DECISION} from '/enum/tealium.enum';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {clearDeepLinkUrl} from '/util/deep-linking/deep-linking.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {withHOC} from '/util/hoc.util';
import {getQualtricsDefaultInstance} from '/util/qualtrics';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import ProfileBusinessSectionContainer from './ProfileBusinessSection/ProfileBusinessSectionContainer';
import {AppInfoSection} from './AppInfoSection';
import Details from './Details';
import {FeedbackSection} from './FeedbackSection';
import Mentors from './Mentors';
import {styles} from './Profile.style';
import {ProfileProps} from './Profile.types';
import {Settings} from './Settings';
import Header from './UserInfo';

export class Profile extends React.PureComponent<ProfileProps> {
  scrollViewRef: any;

  yOffset: number = 0;

  unsubscribeFocus?: () => void;

  // When more than one popover on the page, you need to change the code
  // a little so that onDismiss is not sent when acting with other popovers.
  constructor(props: ProfileProps) {
    super(props);

    const onDismiss = () => {
      getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.exitLink,
      })({
        screenName: SCREEN_NAMES.profile,
        destinationLink: Config.OPTAVIA_PAY_LINK,
        userDecision: USER_DECISION.false,
      });
    };

    // Call the onDismiss function with default or provided callback
    if (this.props.onDismiss) {
      this.props.onDismiss(onDismiss);
    }
  }

  componentDidMount() {
    const {
      hasData = false,
      ts = (key: string) => key,
      fetchData = () => {},
    } = this.props;

    if (!hasData) {
      AccessibilityInfo.isScreenReaderEnabled().then(enabled => {
        if (enabled) {
          AccessibilityInfo.announceForAccessibility(ts('loading, Loading'));
        }
      });
      fetchData();
    }

    const navigation = (this.props as any).navigation;
    if (navigation?.addListener) {
      this.unsubscribeFocus = navigation.addListener('focus', () => {
        if (this.yOffset > 0 && this.scrollViewRef?.scrollTo) {
          requestAnimationFrame(() => {
            this.scrollViewRef.scrollTo({
              x: 0,
              y: this.yOffset,
              animated: false,
            });
          });
        }
      });
    }
  }

  componentWillUnmount() {
    this.unsubscribeFocus?.();
  }

  onShareFeedback = () => {
    // const {userId, coachName} = this.props;
    const SHARE_APP_FEEDBACK_INTERCEPT_ID =
      Config.SHARE_APP_FEEDBACK_INTERCEPT_ID;
    const instance = getQualtricsDefaultInstance();
    instance
      .initFeedback()
      // TODO: Add Qualtrics correct integration with consent policy
      //.then(() => instance.setProperties({coachId: userId, coachName}))
      .then(() => instance.showFeedbackView(SHARE_APP_FEEDBACK_INTERCEPT_ID));
  };

  renderLoading() {
    return (
      <ModalHeader style={{base: styles.base}}>
        <View style={[styles.content, styles.contentLoading]}>
          <LoadingIndicator size="large" />
        </View>
      </ModalHeader>
    );
  }

  getMainScreenComponent() {
    const {
      userId = '',
      hasMentors = false,
      showChatFeature = true,
      onNavigate = () => {},
      signOut = () => {},
      showStorybook = false,
      showTestingOptions = false,
      storybookOpen = () => {},
      popoverShow = () => {},
      onNavigationIconPress = () => {},
      featureFlag = {},
    } = this.props;
    const handleSignOut = () => {
      clearDeepLinkUrl();
      signOut();
    };
    const rightIcons =
      Platform.OS === 'web'
        ? []
        : showChatFeature
        ? ['message', 'search']
        : ['search'];
    const accessibilityLabelsRight = showChatFeature
      ? ['Chat', 'Search']
      : ['Search'];
    const handleShowModal = (modalName: any) => {
      onNavigate('card', {modalName: modalName, modalProps: {userId}});
    };
    const {
      ff_manage_my_site = '',
      ff_healthcare_provider = '',
      ff_supported_languages = '',
      ff_leads_pool_option = '',
      ff_profile_business_section = '',
    } = featureFlag;

    const showBusinessSection =
      shouldReleaseFeature(ff_profile_business_section) ||
      shouldReleaseFeature(ff_manage_my_site) ||
      shouldReleaseFeature(ff_healthcare_provider) ||
      shouldReleaseFeature(ff_supported_languages) ||
      shouldReleaseFeature(ff_leads_pool_option);

    return (
      <View
        testID="profile-screen-header"
        style={
          Platform.OS === 'web'
            ? ((styles as Record<string, ViewStyle>)
                .outerContainer as ViewStyle)
            : {
                flex: 1,
                backgroundColor: 'white',
                marginHorizontal: 'auto',
                padding: 16,
              }
        }>
        <View
          style={
            Platform.OS === 'web'
              ? ((styles as Record<string, ViewStyle>)
                  .profileHeaderBlock as ViewStyle)
              : undefined
          }>
          <NavigationHeader
            rightIcons={rightIcons}
            onIconPress={onNavigationIconPress}
            showBack={false}
            accessibilityLabelsRight={accessibilityLabelsRight}
            testID={ProfilePage.profileScreen}
            style={{
              base: styles.base,
              paddingTop: styles.basePadding.paddingTop,
              ...(Platform.OS === 'web' &&
              (styles as Record<string, ViewStyle>).profileHeaderRow != null
                ? {
                    header: (styles as Record<string, ViewStyle>)
                      .profileHeaderRow as ViewStyle,
                  }
                : {}),
            }}
          />
          {Platform.OS === 'web' && (
            <TouchableOpacity
              style={
                (styles as Record<string, ViewStyle>)
                  .searchIconButton as ViewStyle
              }
              onPress={() =>
                (onNavigationIconPress as (iconName: string) => void)('search')
              }
              accessible
              accessibilityRole="button"
              accessibilityLabel="Search">
              <Icon
                name="search"
                style={
                  (styles as Record<string, ViewStyle>).searchIcon as ViewStyle
                }
              />
            </TouchableOpacity>
          )}
          <Header coachId={userId} accessible accessibilityRole="header" />
        </View>

        <View
          testID="profile-screen"
          style={
            Platform.OS === 'web'
              ? ((styles as Record<string, ViewStyle>)
                  .scrollWrapper as ViewStyle)
              : {flex: 1}
          }>
          <ScrollView
            ref={ref => (this.scrollViewRef = ref)}
            style={styles.content}
            contentInsetAdjustmentBehavior="never"
            keyboardShouldPersistTaps="handled"
            onScroll={event => {
              this.yOffset = event.nativeEvent.contentOffset.y;
            }}
            onContentSizeChange={() => {
              this.scrollViewRef.scrollTo({
                x: 0,
                y: this.yOffset,
                animated: false,
              });
            }}
            scrollEventThrottle={16}>
            <Details
              coachId={userId}
              nativeID="detailsSection"
              accessibilityTraversalAfter="coachHeader"
            />
            {hasMentors && <Mentors coachId={userId} />}

            {showBusinessSection && (
              <ProfileBusinessSectionContainer
                featureFlag={featureFlag}
                userId={userId}
                onNavigate={onNavigate}
              />
            )}

            <Settings
              popoverShow={popoverShow}
              onSettingPress={handleShowModal}
              currentScreen={SCREEN_NAMES.profile}
            />
            <FeedbackSection
              onShareFeedback={() => this.onShareFeedback()}
              onFeedbackPress={handleShowModal}
            />

            <AppInfoSection showVersion={Platform.OS !== 'web'} />
            <View style={styles.logoutContainer} accessible={false}>
              {Platform.OS !== 'web' && showStorybook && (
                <ProfileActiveAttribute
                  iconName={'learn'}
                  label={'Storybook'}
                  onPress={() => defer(storybookOpen)}
                  style={{}}
                  isLastElement={false}
                  useCompetency={false}
                  testID={null}
                />
              )}
              {Platform.OS !== 'web' && showTestingOptions && (
                <ProfileActiveAttribute
                  iconName={'learn'}
                  label={'Testing Options'}
                  onPress={() => handleShowModal(TESTING_OPTIONS)}
                  style={{}}
                  isLastElement={false}
                  useCompetency={false}
                  testID={null}
                />
              )}
              <ProfileActiveAttribute
                testID={ProfilePage.profileLogOut}
                iconName={'logout'}
                isLastElement={true}
                style={{}}
                useCompetency={false}
                label={
                  <Text
                    variant="small"
                    emphasis="high"
                    style={styles.itemText}
                    useCompetency>
                    {'Log out of '}
                    <Text variant="heading" emphasis="high" useCompetency>
                      OPTA
                    </Text>
                    VIA Connect
                  </Text>
                }
                onPress={handleSignOut}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    );
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  render() {
    const {hasData} = this.props;
    const MainScreen = this.getMainScreenComponent();
    const Loading = this.renderLoading();

    return (
      <KeyboardAvoidingView style={styles.keyboardViewContainer}>
        {hasData ? MainScreen : Loading}
      </KeyboardAvoidingView>
    );
  }
}
export default withHOC(withSafeArea, withTranslation('profile'))(Profile);
